import { FC, useEffect } from 'react';
import { Select, MenuItem } from '@mui/material';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchCostCentres } from '../../store/costCentreStore';
import { ICostCentre } from '../../utils/costCentre.types';

interface CostCentreSelectorProps {
  value?: string | null;
  onChange: (value: string | null) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
}

/**
 * Reusable Cost Centre Selector Component
 * Used in voucher forms for selecting cost centre per line item
 */
const CostCentreSelector: FC<CostCentreSelectorProps> = ({
  value,
  onChange,
  label = "Cost Centre",
  placeholder = "Select Cost Centre (Optional)",
  disabled = false,
  error = false,
  helperText,
  required = false
}) => {
  const dispatch = useAppDispatch();
  const { activeCostCentres } = useAppSelector(state => state.costCentres);

  // Load active cost centres on mount
  useEffect(() => {
    dispatch(fetchCostCentres({ activeOnly: true }));
  }, [dispatch]);

  return (
    <Select
      disableUnderline
      MenuProps={{
        PaperProps: {
          sx: {
            border: "1.5px solid #e9ecf1",
            boxShadow: 'none',
            borderRadius: '5px'
          }
        }
      }}
      sx={{
        boxShadow: "none",
        width: 250,
        ml: '10px !important',
        pt: '0px !important',
        pl: '10px !important',
        '& .MuiOutlinedInput-input': { p: 0, m: 0 },
        '& .MuiSelect-select': { p: 0 },
        ".MuiOutlinedInput-notchedOutline": { border: 0 },
        "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          border: 0,
        },
        "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
          border: 0,
        },
      }}
      className='c-text-field-3'
      variant="outlined"
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      displayEmpty
    >
      <MenuItem value="">{placeholder}</MenuItem>
      {activeCostCentres.map((cc: ICostCentre) => (
        <MenuItem key={cc._id} value={cc._id}>
          {cc.name}{cc.alias ? ` (${cc.alias})` : ''}
        </MenuItem>
      ))}
    </Select>
  );
};

export default CostCentreSelector;
